import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CompanyModel } from '@data-layer/models/companies';
import { CompaniesService } from '@services/companies.service';
import { enumDocumentRootEntityType } from '@data-layer/enums/documents';
import { EntrySummaryStateService } from '@shared/es-mutual/services';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { enumOcrStatus } from '@data-layer/enums/common';
import { UntypedFormControl } from '@angular/forms';

@Component({
  selector: 'es-invoice-add-ocr',
  templateUrl: './es-invoice-add-ocr.component.html',
  styleUrls: ['./es-invoice-add-ocr.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsInvoiceAddOcrComponent implements OnInit, OnDestroy {
  @Input() public existingInvoice: InvoiceModel;
  @Input() public billOfLadingId: number;
  @Input() public rootEntityId: number;
  @Input() public rootEntityType: enumDocumentRootEntityType;
  @Input() public existingFile: File;
  @Input() public isShowWarning: boolean;
  @Input() public isWithOcr: boolean = true;
  @Output() public editManually: EventEmitter<number> = new EventEmitter<number>();
  @Output() public ocrCompleted: EventEmitter<number> = new EventEmitter<number>();

  public company: CompanyModel;
  public invoiceId: number;
  public isLoading: boolean;

  public skipControl: UntypedFormControl;
  public documentUploadOcrId: string;

  private POLLING_TIMEOUT: number = 1500;
  private MAX_POLLING_RETRY: number = 80;

  private ocrStatus: enumOcrStatus;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private companiesService: CompaniesService,
    private entrySummaryStateService: EntrySummaryStateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany();

    this.skipControl = new UntypedFormControl(!this.isWithOcr);

    this.entrySummaryStateService.currentOCRState$.pipe(takeUntil(this.destroy$)).subscribe((currentOCRState: enumOcrStatus) => {
      this.ocrStatus = currentOCRState;
      this.cdr.markForCheck();
    });

    this.documentUploadOcrId = 'file-input-ocr-' + this.billOfLadingId;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onUploadFile(file: File): void {
    if (!this.company) {
      return;
    }

    this.isLoading = true;
    this.isShowWarning = false;

    this.entrySummaryStateService
      .processInvoiceOCR(
        this.existingInvoice,
        this.billOfLadingId,
        this.invoiceId,
        this.rootEntityId,
        this.rootEntityType,
        this.company,
        file,
        this.POLLING_TIMEOUT,
        this.MAX_POLLING_RETRY,
        !this.skipControl.value && this.isWithOcr,
      )
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(
        (data: { status: enumOcrStatus; invoiceId: number }) => {
          this.isLoading = false;
          this.invoiceId = data.invoiceId;

          if ([enumOcrStatus.PARTIALLY_PROCESSED, enumOcrStatus.PROCESSED, enumOcrStatus.CANCELED].includes(data.status)) {
            this.ocrCompleted.emit(this.invoiceId);
          }

          if (!data.status || data.status === enumOcrStatus.EXTRACTION_FAILED) {
            this.isShowWarning = true;
          }

          this.cdr.markForCheck();
        },
        () => {
          this.isLoading = false;
          this.isShowWarning = true;

          this.cdr.markForCheck();
        },
      );
  }

  public get isConvertingInvoice(): boolean {
    return !this.isExtractingInvoice && !this.isClassifyingInvoice;
  }

  public get isExtractingInvoice(): boolean {
    return [
      enumOcrStatus.PROCESSED,
      enumOcrStatus.PARTIALLY_PROCESSED,
      enumOcrStatus.EXTRACTION_FAILED,
      enumOcrStatus.EXTRACTING_DATA,
    ].includes(this.ocrStatus);
  }

  public get isClassifyingInvoice(): boolean {
    return this.ocrStatus === enumOcrStatus.CLASSIFYING_LINE_ITEMS;
  }
}
